/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Plus, Plane, RefreshCw } from "lucide-react";
import { buildAircraftView, sortByUrgency, summarizeFleet } from "../../lib/aircraftView";
import { computeHealthScore } from "../../lib/fleetHealth";
import AircraftCard from "./di/AircraftCard";
import AircraftForm from "./di/AircraftForm";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function FleetSummary({ total, counts, onRefresh, refreshing }) {
  return (
    <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-[12px]" data-testid="di-fleet-summary">
      <span className="font-semibold text-slate-100">{plural(total, "aeronave", "aeronaves")}</span>
      {counts.grounded > 0 && <span className="text-red-300">{counts.grounded === 1 ? "1 não pode voar" : `${counts.grounded} não podem voar`}</span>}
      {counts.warn > 0 && <span className="text-amber-300">{counts.warn === 1 ? "1 pode voar, com avisos" : `${counts.warn} podem voar, com avisos`}</span>}
      {counts.ok > 0 && <span className="text-emerald-300">{counts.ok === 1 ? "1 pode voar, sem avisos" : `${counts.ok} podem voar, sem avisos`}</span>}
      {counts.retired > 0 && <span className="text-slate-400">{plural(counts.retired, "retirada", "retiradas")}</span>}
      <button type="button" onClick={onRefresh} disabled={refreshing} className="ml-auto text-slate-500 hover:text-white" title="Atualizar" aria-label="Atualizar" data-testid="di-fleet-refresh">
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
}

export default function AircraftPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [extras, setExtras] = useState({});
  const [liveAlerts, setLiveAlerts] = useState(null);   // { [aircraftId]: alert[] }; null enquanto carrega
  const [alertsFailed, setAlertsFailed] = useState(false);
  // Todos os cartões aparecem abertos; só se fecha o que se fechar à mão
  const [collapsed, setCollapsed] = useState(() => new Set());

  // `silent`: atualiza sem o ecrã "A carregar…" (depois de editar, fechar uma janela ou carregar em atualizar)
  const refresh = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/di/aircraft`, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar aeronaves");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAlerts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/aircraft-alerts-live`, { withCredentials: true });
      const map = {};
      (r.data?.aircraft || []).forEach((a) => { map[a.id] = a.alerts || []; });
      setLiveAlerts(map);
      setAlertsFailed(false);
    } catch (e) {
      setLiveAlerts({});
      setAlertsFailed(true);
    }
  }, []);

  const reload = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([refresh(true), loadAlerts()]);
    setRefreshing(false);
  }, [refresh, loadAlerts]);

  useEffect(() => { refresh(); loadAlerts(); }, [refresh, loadAlerts]);

  useEffect(() => {
    if (!items.length) { setExtras({}); return; }
    let cancelled = false;
    const today = new Date().toISOString().slice(0, 10);
    Promise.all(items.map(async (a) => {
      const [insArr, ms, g, maint] = await Promise.all([
        axios.get(`${API}/di/aircraft/${a.id}/insurances`, { withCredentials: true }).then((r) => r.data).catch(() => []),
        axios.get(`${API}/di/aircraft/${a.id}/maintenance/summary`, { withCredentials: true }).then((r) => r.data).catch(() => null),
        axios.get(`${API}/di/aircraft/${a.id}/logbook-gaps`, { withCredentials: true }).then((r) => r.data).catch(() => null),
        axios.get(`${API}/di/aircraft/${a.id}/maintenance`, { withCredentials: true }).then((r) => r.data).catch(() => null),
      ]);
      const active = (insArr || []).filter((x) => x.policy_start && x.policy_end && x.policy_start <= today && x.policy_end >= today);
      const insurance = active.length
        ? active.sort((a2, b2) => (b2.policy_end || "").localeCompare(a2.policy_end || ""))[0]
        : (insArr || []).length
          ? [...insArr].sort((a2, b2) => (b2.policy_end || "").localeCompare(a2.policy_end || ""))[0]
          : null;
      return [a.id, { insurance, maintSummary: ms, gapInfo: g, maintItems: maint?.items || [], perDayMin: maint?.usage?.daily_engine_min || 0 }];
    })).then((pairs) => {
      if (cancelled) return;
      const next = {};
      pairs.forEach(([k, v]) => { next[k] = v; });
      setExtras(next);
    });
    return () => { cancelled = true; };
  }, [items]);

  const remove = async (a) => {
    if (!window.confirm(`Eliminar ${a.registration}? Os alertas associados serão apagados.`)) return;
    try {
      await axios.delete(`${API}/di/aircraft/${a.id}`, { withCredentials: true });
      toast.success("Aeronave eliminada");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  // Só se mostra um estado quando já chegaram os alertas e os dados do seguro e da caderneta desta aeronave:
  // antes disso "A calcular…" (nunca um "Pode voar" por falta de dados).
  const rows = items.map((a) => {
    const ex = extras[a.id];
    const view = liveAlerts && ex
      ? buildAircraftView({ aircraft: a, insurance: ex.insurance, gapInfo: ex.gapInfo, alerts: liveAlerts[a.id] || [] })
      : null;
    const health = ex ? computeHealthScore({ aircraft: a, insurance: ex.insurance, maintSummary: ex.maintSummary }) : null;
    return { aircraft: a, extras: ex || {}, view, health };
  });
  const ready = rows.length > 0 && rows.every((r) => r.view);
  if (ready) rows.sort(sortByUrgency);
  const counts = summarizeFleet(rows.filter((r) => r.view).map((r) => r.view));

  return (
    <div className="space-y-4" data-testid="di-aircraft-panel">
      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-orange-300">
            <Plane className="w-5 h-5" /> Aeronaves
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-end gap-3 flex-wrap">
            <Button size="sm" className="btn-cockpit" onClick={() => { setEditing(null); setCreating(true); }} data-testid="di-ac-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Nova aeronave
            </Button>
          </div>
          {ready && <FleetSummary total={rows.length} counts={counts} onRefresh={reload} refreshing={refreshing} />}
          {alertsFailed && (
            <div className="text-[11px] text-amber-300" data-testid="di-alerts-failed">
              Não foi possível carregar todos os avisos. Aparecem só os bloqueios e os avisos do estado da aeronave.
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="py-10 text-center text-slate-500 text-sm">A carregar…</div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-slate-500 text-sm border border-dashed border-cockpit-border rounded">
          Nenhuma aeronave registada. Carrega em <strong>Nova aeronave</strong>.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start" data-testid="di-ac-list">
          {rows.map((row) => (
            <AircraftCard
              key={row.aircraft.id}
              aircraft={row.aircraft}
              extras={row.extras}
              view={row.view}
              health={row.health}
              expanded={!collapsed.has(row.aircraft.id)}
              onToggleExpand={() => setCollapsed((prev) => {
                const next = new Set(prev);
                if (next.has(row.aircraft.id)) next.delete(row.aircraft.id); else next.add(row.aircraft.id);
                return next;
              })}
              onEdit={() => { setEditing(row.aircraft); setCreating(false); }}
              onDelete={() => remove(row.aircraft)}
              onChanged={reload}
            />
          ))}
        </div>
      )}

      {(creating || editing) && (
        <AircraftForm
          aircraft={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}
